import { MiniFace } from "./MiniFace";
import { SUGGESTED_PLAYERS } from "@unfaithful/shared";
import { useEffect, useState, type FormEvent } from "react";
import { CLOAK_COLOURS, SILHOUETTE_NAMES, silhouetteUrl, photoOf, readJson, shrinkPhoto, titleCase } from "@unfaithful/client";
import {
  CLOAKS,
  LIMITS,
  SIGILS,
  isValidCastleCode,
  normaliseCastleCode,
  FACT_MAX,
  type Cloak,
  type PlayerView,
  type Sigil,
} from "@unfaithful/shared";
import { GamePanel } from "./GamePanel";
import { VoicePanel } from "./VoicePanel";
import { Hub } from "./Hub";
import { FFPhone, hasSavedFinalFew } from "./finalfew/FFPhone";
import { usePlayerSession, type SessionStatus } from "./usePlayerSession";

export function App() {
  // Which game a code belongs to: from the link, a saved seat, or the home screen's lookup.
  const [game, setGame] = useState<string | null>(() => new URLSearchParams(location.search).get("g") ?? (!codeFromUrl() && hasSavedFinalFew() ? "finalfew" : null));
  const [ffCode, setFfCode] = useState<string>(() => codeFromUrl());
  if (game === "finalfew") return <FFPhone code={ffCode} />;
  return <UnfaithfulApp onOtherGame={(g, code) => (setFfCode(code), setGame(g))} />;
}

function UnfaithfulApp({ onOtherGame }: { onOtherGame: (game: string, code: string) => void }) {
  const session = usePlayerSession();
  // playpop.tv opens on the PlayPop home screen. A code in the link (or one
  // typed there) goes straight to that game's join screen.
  const [chosenCode, setChosenCode] = useState<string | null>(() => codeFromUrl() || null);
  const atHome = !chosenCode && !session.view && !session.restoring && !session.removedReason;
  // A code in the link might belong to another game: ask once.
  useEffect(() => {
    const code = codeFromUrl();
    if (!code) return;
    fetch(`/api/castles/${code}`, { cache: "no-store" })
      .then((r) => (r.ok ? (r.json() as Promise<{ found: boolean; game?: string }>) : null))
      .then((found) => {
        if (found?.found && found.game && found.game !== "unfaithful") onOtherGame(found.game, code);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (atHome) return <Hub onCode={(g, code) => (g === "unfaithful" ? setChosenCode(code) : onOtherGame(g, code))} />;
  return (
    <div className="phone hall-bg grain">
      <ConnectionBar status={session.status} />
      {session.view && session.view.castle.phase !== "lobby" && session.view.castle.game ? (
        <GamePanel
          view={session.view}
          clockOffset={session.clockOffset}
          act={session.act}
          requestUpload={session.requestUpload}
          onLeave={() => void session.leave()}
          onEnd={() => void session.end()}
          voice={<VoicePanel compact onJoin={session.joinVoice} onLeave={session.leaveVoice} />}
        />
      ) : session.view ? (
        <Seated view={session.view} session={session} />
      ) : session.restoring ? (
        <main className="screen centred">
          <p className="quiet">Finding your seat…</p>
        </main>
      ) : (
        <Join initialCode={chosenCode ?? ""} onJoin={session.join} removedReason={session.removedReason} online={session.status === "online"} onBack={() => setChosenCode(null)} />
      )}
    </div>
  );
}

function codeFromUrl(): string {
  const params = new URLSearchParams(location.search);
  const fromQuery = params.get("c") ?? "";
  const fromPath = location.pathname.replace(/^\//, "");
  const candidate = fromQuery || fromPath;
  return isValidCastleCode(candidate) ? normaliseCastleCode(candidate) : "";
}

function Join({
  initialCode,
  onJoin,
  removedReason,
  online,
  onBack,
}: {
  initialCode: string;
  onJoin: (code: string, name: string) => Promise<string | null>;
  removedReason: string | null;
  online: boolean;
  onBack: () => void;
}) {
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState(() => readJson<{ name?: string }>("unfaithful.phone.seat")?.name ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeReady = isValidCastleCode(code);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!codeReady) return setError("Castle codes are 4 letters. Check the TV.");
    if (!name.trim()) return setError("Enter the name everyone knows you by.");
    setBusy(true);
    setError(await onJoin(code, name));
    setBusy(false);
  };

  return (
    <main className="screen">
      <header className="masthead">
        <h1 className="wordmark" aria-label="Unfaithful">
          <span className="wordmark-un" aria-hidden>Un</span>
          <span className="wordmark-faithful" aria-hidden>faithful</span>
        </h1>
        {removedReason ? <p className="notice">{removedReason}</p> : <p className="quiet">Some of you are lying.</p>}
      </header>
      <form className="join" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">Castle code</span>
          <input
            className="code-input"
            value={code}
            onChange={(e) => setCode(normaliseCastleCode(e.target.value).slice(0, 4))}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={8}
            placeholder="RAVN"
            aria-describedby="code-help"
          />
          <span id="code-help" className="field-help">The four letters on the TV</span>
        </label>
        <label className="field">
          <span className="field-label">Your name</span>
          <input
            className="text-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={LIMITS.nameMaxLength}
            autoComplete="nickname"
            autoCapitalize="words"
            enterKeyHint="go"
            placeholder="What friends call you"
          />
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="dock">
          <button className="primary wax-button" type="submit" disabled={busy || !online}>
            {busy ? "Entering…" : "Enter the castle"}
          </button>
          <button className="text-button" type="button" onClick={onBack}>
            Back to PlayPop
          </button>
        </div>
      </form>
    </main>
  );
}

function Seated({ view, session }: { view: PlayerView; session: ReturnType<typeof usePlayerSession> }) {
  const { me, castle } = view;
  const [picking, setPicking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const others = castle.players.filter((p) => p.id !== me.id);
  const needed = Math.max(0, castle.minPlayers - castle.players.length);
  // Shared with the TV: change it here and the TV changes too.
  const familyMode = castle.lobby?.familyMode ?? false;
  const [handingOver, setHandingOver] = useState(false);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  return (
    <main className="screen seated">
      <section className="me" aria-label="You">
        <span className="gilt-frame" style={{ "--cloak": CLOAK_COLOURS[me.cloak] } as React.CSSProperties}>
          <span className="canvas" aria-hidden>
            {photoOf(castle, me.id) ? <img className="face-photo" src={photoOf(castle, me.id)!} alt="" /> : <img className="face-photo face-silhouette" src={silhouetteUrl(me.sigil, me.cloak)} alt="" />}
          </span>
        </span>
        <h1 className="my-name">{me.name}</h1>
        <p className="quiet">
          Your look: {SILHOUETTE_NAMES[me.sigil].toLowerCase()}, in {titleCase(me.cloak).toLowerCase()}.
        </p>
      </section>

      <p className="look-up">You're in. Look up at the TV.</p>
      <p className="quiet centred-text">
        {needed > 0 ? `Waiting for ${needed} more ${needed === 1 ? "guest" : "guests"}.` : "Waiting for the Host to begin."}
      </p>

      {castle.photos?.enabled && <SelfieButton hasPhoto={!!photoOf(castle, me.id)} setPhoto={session.setPhoto} onMessage={setMessage} />}

      <FactsForm saved={castle.facts?.[me.id] ?? []} save={session.setFacts} onMessage={setMessage} />

      <VoicePanel onJoin={session.joinVoice} onLeave={session.leaveVoice} />

      {picking ? (
        <StylePicker
          view={view}
          onCancel={() => setPicking(false)}
          onChoose={async (sigil, cloak) => {
            const err = await session.restyle(sigil, cloak);
            if (err) setMessage(err);
            else setPicking(false);
          }}
        />
      ) : (
        <button className="secondary moon-button" onClick={() => setPicking(true)}>
          Change your silhouette and colour
        </button>
      )}

      {me.isHost && (
        <section className="host-tools host-bar" aria-label="Host controls">
          <header className="host-bar-head">
            <span className="host-crown" aria-hidden>♛</span>
            <div>
              <h2 className="host-bar-title">Host controls</h2>
              <p className="host-bar-sub">Only you see these. Changes show on the TV straight away.</p>
            </div>
          </header>
          {needed > 0 && <p className="quiet">The game settings appear once {castle.minPlayers} guests are in.</p>}
          {needed === 0 && (
            <>
              <h3 className="host-step">1. Who's playing?</h3>
              <button className={`mode-choice ${familyMode ? "is-on" : ""}`} role="switch" aria-checked={familyMode} onClick={async () => setMessage(await session.setLobby({ familyMode: !familyMode }))}>
                <strong>Family mode: {familyMode ? "on" : "off"}</strong>
                <span>Softer words: nobody is murdered, they're eliminated. Same rules.</span>
              </button>
              <h3 className="host-step">2. When everyone's ready</h3>
              {castle.players.length < SUGGESTED_PLAYERS && (
                <p className="quiet">
                  {castle.players.length} guests is enough, but {SUGGESTED_PLAYERS} or more makes a longer, better game{castle.testing?.allowed ? ": add AI guests to fill seats" : ""}.
                </p>
              )}
              <button className="primary wax-button" onClick={async () => setMessage(await session.act({ type: "start" }))}>
                Begin the game
              </button>
            </>
          )}
          <button
            className="secondary moon-button"
            onClick={async () => setMessage((await session.setPhotosEnabled(!castle.photos?.enabled)) ?? (castle.photos?.enabled ? "Photos are off. Everyone's selfie has been deleted." : "Photos are on."))}
          >
            {castle.photos?.enabled ? "Switch photos off" : "Allow selfies"}
          </button>
          {castle.testing?.allowed && castle.players.length < castle.maxPlayers && (
            <button className="secondary moon-button" onClick={async () => setMessage((await session.addTestGuests(1)) ?? "An AI guest has arrived.")}>
              Add an AI guest
            </button>
          )}
          <button
            className="secondary moon-button"
            onClick={async () => setMessage((await session.ringBell()) ?? "The bell rings on every screen.")}
          >
            Ring the bell to test sound
          </button>
          {others.length > 0 &&
            (handingOver ? (
              <div className="handover">
                <p className="quiet">Who should host instead? They get these controls; you become a guest.</p>
                <ul className="guest-list">
                  {others.filter((p) => p.connected).map((p) => (
                    <li key={p.id}>
                      <span>
                        <MiniFace p={p} /> {p.name}
                      </span>
                      <button
                        className="text-button"
                        onClick={async () => {
                          const err = await session.transferHost(p.id);
                          setMessage(err ?? `${p.name} is the Host now.`);
                          setHandingOver(false);
                        }}
                      >
                        Make Host
                      </button>
                    </li>
                  ))}
                </ul>
                <button className="text-button" onClick={() => setHandingOver(false)}>
                  Keep hosting
                </button>
              </div>
            ) : (
              <button className="secondary moon-button" onClick={() => setHandingOver(true)}>
                Hand hosting to someone else
              </button>
            ))}
          {others.length > 0 && (
            <ul className="guest-list">
              {others.map((p) => (
                <li key={p.id}>
                  <span>
                    <MiniFace p={p} /> {p.name}
                    {!p.connected && <span className="quiet"> (signal lost)</span>}
                  </span>
                  <button className="text-button" onClick={async () => setMessage(await session.kick(p.id))}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {message && <p className="toast" role="status">{message}</p>}

      <button className="text-button leave" onClick={() => void session.leave()}>
        Leave the castle
      </button>
    </main>
  );
}

function StylePicker({
  view,
  onChoose,
  onCancel,
}: {
  view: PlayerView;
  onChoose: (sigil: Sigil, cloak: Cloak) => void;
  onCancel: () => void;
}) {
  const [sigil, setSigil] = useState<Sigil>(view.me.sigil);
  const [cloak, setCloak] = useState<Cloak>(view.me.cloak);
  const others = view.castle.players.filter((p) => p.id !== view.me.id);
  const takenSigils = new Set(others.map((p) => p.sigil));
  const takenCloaks = new Set(others.map((p) => p.cloak));

  return (
    <section className="picker" aria-label="Choose your silhouette and colour">
      <h2 className="section-title">Your silhouette</h2>
      <p className="quiet picker-help">Your shadow portrait on the TV, whenever you don't have a photo.</p>
      <div className="swatches" role="radiogroup" aria-label="Silhouette">
        {SIGILS.map((s) => (
          <button
            key={s}
            role="radio"
            aria-checked={sigil === s}
            aria-label={SILHOUETTE_NAMES[s]}
            className={`swatch ${sigil === s ? "is-chosen" : ""}`}
            disabled={takenSigils.has(s)}
            onClick={() => setSigil(s)}
          >
            <img className="swatch-face" src={silhouetteUrl(s, cloak)} alt="" />
          </button>
        ))}
      </div>
      <h2 className="section-title">Your colour</h2>
      <p className="quiet picker-help">Your portrait frame, your seal and your marks on the TV.</p>
      <div className="swatches" role="radiogroup" aria-label="Colour">
        {CLOAKS.map((c) => (
          <button
            key={c}
            role="radio"
            aria-checked={cloak === c}
            aria-label={titleCase(c)}
            className={`swatch cloak ${cloak === c ? "is-chosen" : ""}`}
            style={{ "--cloak": CLOAK_COLOURS[c] } as React.CSSProperties}
            disabled={takenCloaks.has(c)}
            onClick={() => setCloak(c)}
          />
        ))}
      </div>
      <div className="picker-actions">
        <button className="primary wax-button" onClick={() => onChoose(sigil, cloak)}>
          The {sigil}, in {titleCase(cloak)}
        </button>
        <button className="text-button" onClick={onCancel}>
          Keep my current look
        </button>
      </div>
    </section>
  );
}

function ConnectionBar({ status }: { status: SessionStatus }) {
  if (status === "online") return null;
  if (status === "stuck" || status === "locked") {
    return (
      <div className="connection is-stuck" role="alert">
        <p>{status === "locked" ? "This phone needs the test-site password again." : "Still can't reach the game."}</p>
        <button className="primary wax-button" onClick={() => location.reload()}>
          {status === "locked" ? "Open the game again" : "Reconnect"}
        </button>
        <p className="quiet">Your seat is kept for you.</p>
      </div>
    );
  }
  return (
    <p className="connection" role="status">
      {status === "offline" ? "Signal lost. Reconnecting…" : "Connecting…"}
    </p>
  );
}

/**
 * Optional selfie. The phone's camera opens (front-facing where it can); the
 * photo is shrunk on the phone before it's sent, shown in your portrait frame
 * on the TV, and deleted when the castle closes.
 */
function SelfieButton({
  hasPhoto,
  setPhoto,
  onMessage,
}: {
  hasPhoto: boolean;
  setPhoto: (image: string | null) => Promise<string | null>;
  onMessage: (m: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const choose = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const err = await setPhoto(await shrinkPhoto(file));
      onMessage(err ?? "Lovely. You're on the TV.");
    } catch (e) {
      onMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="selfie">
      <label className={`secondary moon-button selfie-take ${busy ? "is-busy" : ""}`}>
        {busy ? "Sending…" : hasPhoto ? "Retake your selfie" : "Add a selfie (optional)"}
        <input type="file" accept="image/*" capture="user" hidden onChange={(e) => void choose(e.target.files?.[0])} />
      </label>
      {hasPhoto && (
        <button className="text-button" onClick={async () => onMessage((await setPhoto(null)) ?? "Photo removed. Your silhouette is back.")}>
          Remove my photo
        </button>
      )}
      <p className="quiet selfie-note">Shown in your frame on the TV during this game, then deleted.</p>
    </div>
  );
}

/**
 * Three facts about you, optional. The host reads them out when you arrive and
 * may bring them up later. Deleted when the castle closes.
 */
function FactsForm({ saved, save, onMessage }: { saved: string[]; save: (f: string[]) => Promise<string | null>; onMessage: (m: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(() => [saved[0] ?? "", saved[1] ?? "", saved[2] ?? ""]);
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <div className="facts">
        {saved.length > 0 && (
          <ul className="facts-saved">
            {saved.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
        <button className="secondary moon-button" onClick={() => setOpen(true)}>
          {saved.length > 0 ? "Change your three facts" : "Three facts about you (optional)"}
        </button>
      </div>
    );
  }
  return (
    <form
      className="facts"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        const err = await save(draft);
        setBusy(false);
        onMessage(err ?? "Saved. Watch the TV.");
        if (!err) setOpen(false);
      }}
    >
      <p className="quiet">Three things about you. The host will read them out, so keep them fun and family-friendly.</p>
      {["Hates mornings", "Never loses at Monopoly", "Scared of geese"].map((hint, i) => (
        <input
          key={i}
          className="text-input"
          value={draft[i]}
          maxLength={FACT_MAX}
          placeholder={`e.g. ${hint}`}
          onChange={(e) => setDraft((d) => d.map((v, k) => (k === i ? e.target.value : v)))}
        />
      ))}
      <button className="primary wax-button" disabled={busy}>
        {busy ? "Saving…" : "Save"}
      </button>
      <p className="quiet selfie-note">Deleted when the game ends.</p>
    </form>
  );
}
